import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, first, map } from 'rxjs/operators';
import * as R from 'ramda';

type RequestData = any;
type ResponseData = any;

type ActiveRequest = Observable<ResponseData>;

interface CachedResponsesCollection {
  [serviceName: string]: {
    [key: string]: ResponseData;
  };
}

interface ActiveRequestsCollection {
  [serviceName: string]: {
    [key: string]: ActiveRequest;
  };
}

interface ActiveRequestFlags {
  [serviceName: string]: {
    [key: string]: boolean;
  };
}

type Callback = () => Observable<ResponseData>;

@Injectable({ providedIn: 'root' })
export class CachedRequestsService {
  private cachedResponses: CachedResponsesCollection = {};
  private activeRequests: ActiveRequestsCollection = {};
  private activeRequestFlags$ = new BehaviorSubject<ActiveRequestFlags>({});

  get(serviceName: string, requestData: RequestData, callback: Callback): Observable<ResponseData> {
    const cacheKey = JSON.stringify(requestData);

    const cachedResponseData = this.getCachedRequest(serviceName, cacheKey);

    if (cachedResponseData) {
      return of(cachedResponseData);
    }

    const activeRequest: ActiveRequest = this.getActiveRequest(serviceName, cacheKey);

    if (activeRequest) {
      return activeRequest;
    }

    const observable = callback().pipe(
      first(),
      catchError(() => of(null)),
      map((responseData: ResponseData): ResponseData => {
        if (responseData) {
          this.saveToCache(serviceName, cacheKey, responseData);
        }

        this.removeActiveRequest(serviceName, cacheKey);

        return responseData;
      }),
    );

    this.addActiveRequest(serviceName, cacheKey, observable);

    return observable;
  }

  hasActiveRequests(serviceName: string): Observable<boolean> {
    return this.activeRequestFlags$.pipe(
      map(activeRequestFlags =>
        Object.keys(activeRequestFlags?.[serviceName] || {}).reduce(
          (acc, cacheKey) => acc || activeRequestFlags?.[serviceName]?.[cacheKey],
          false,
        ),
      ),
    );
  }

  private getCachedRequest(serviceName: string, cacheKey: string): ResponseData {
    return this.cachedResponses?.[serviceName]?.[cacheKey];
  }

  private saveToCache(serviceName: string, cacheKey: string, responseData: ResponseData) {
    if (!this.cachedResponses[serviceName]) {
      this.cachedResponses[serviceName] = {};
    }

    this.cachedResponses[serviceName][cacheKey] = responseData;
  }

  private getActiveRequest(serviceName: string, cacheKey: string): ActiveRequest {
    return this.activeRequests?.[serviceName]?.[cacheKey];
  }

  private addActiveRequest(serviceName: string, cacheKey: string, observable: Observable<ResponseData>) {
    const activeRequestFlags = this.activeRequestFlags$.getValue() || {};

    if (!this.activeRequests[serviceName]) {
      this.activeRequests[serviceName] = {};
    }

    this.activeRequests[serviceName][cacheKey] = observable;
    this.activeRequestFlags$.next({
      ...activeRequestFlags,
      [serviceName]: {
        ...(activeRequestFlags?.[serviceName] || {}),
        [cacheKey]: true,
      },
    });
  }

  private removeActiveRequest(serviceName: string, cacheKey: string) {
    const activeRequestFlags = this.activeRequestFlags$.getValue() || {};

    delete this.activeRequests[serviceName][cacheKey];
    this.activeRequestFlags$.next({
      ...activeRequestFlags,
      [serviceName]: R.omit([cacheKey], activeRequestFlags?.[serviceName] || {}),
    });
  }
}
